import { useEffect, useRef } from "react";

/**
 * dismissibleStack — the app's one register of "things Back should close first".
 *
 * WHY A STACK AND NOT PER-COMPONENT `if (open)` CHECKS
 * ---------------------------------------------------
 * Android's Back is a single event with a single winner. If every sheet checks
 * its own `open` flag when Back fires, two overlapping sheets both claim the
 * press: one closes the wrong surface, or both close at once, and which one you
 * get depends on effect-registration order — i.e. on render internals nobody is
 * looking at. `ProfileAccountSheet` already had one of these ad-hoc handlers
 * (a `history.pushState` + `popstate` pair) and it leaked one history entry per
 * open, because nothing ever popped the entry it pushed.
 *
 * So overlays register here instead. Back pops exactly one — the last one
 * opened — which is the only ordering a user can predict.
 *
 * Module-level rather than React context on purpose: the back handler is
 * registered from a Capacitor callback that fires outside React's render
 * cycle, and a plain array read is the same value the user is looking at. A
 * context value captured in a closure is not.
 *
 * Nothing here imports `@capacitor/*` — this module is pure DOM-free JS and is
 * just as useful on web (Escape, browser Back) if that is ever wired up.
 *
 * WEB ESCAPE (2026-08). It is now wired up, and deliberately NOT from here.
 * `escapeToDismiss.js` owns the `document` listener and reaches in through
 * `subscribeDismissibles` below. That keeps this file what its docblock claims
 * — plain JS with no `document`, no `window`, no React outside the one hook —
 * so it stays testable and usable from the Capacitor callback, which runs
 * outside React and (on some platforms) before the DOM the listener needs.
 *
 * `subscribeDismissibles` is a depth notification, nothing more: it carries a
 * count, not entries, and it exists so a consumer can attach its listener only
 * while there is something to dismiss instead of sitting on `document` for the
 * whole session.
 */

/** LIFO. Index 0 is the oldest registration; the last element is the top. */
let stack = [];
let nextId = 0;

/** Depth-change subscribers. See `subscribeDismissibles`. */
let subscribers = new Set();

/**
 * Tell every subscriber the current depth.
 *
 * Iterates a copy: a subscriber is allowed to unsubscribe (or register another
 * overlay) from inside its own callback, and mutating the live Set mid-loop
 * would skip whoever came after it.
 */
const notifyDepth = () => {
  const count = stack.length;
  [...subscribers].forEach((onDepthChange) => onDepthChange(count));
};

/**
 * Watch how many overlays are registered. Returns the unsubscribe function.
 *
 * Called synchronously on every push and on every unregister that actually
 * removed something — never on `dismissTopDismissible`, which by design does
 * not change the stack. NOT called on subscribe: a fresh subscriber that cares
 * about the current depth reads `dismissibleCount()` itself, which keeps this
 * a change notification rather than a half-state-half-event hybrid.
 */
export const subscribeDismissibles = (onDepthChange) => {
  if (typeof onDepthChange !== "function") {
    throw new TypeError("subscribeDismissibles requires a function");
  }
  subscribers.add(onDepthChange);
  return () => {
    subscribers.delete(onDepthChange);
  };
};

/**
 * Register an overlay as dismissible. Returns the unregister function, so the
 * common use is `useEffect(() => pushDismissible(close, "sheet"), [open])`.
 *
 * `dismiss` is invoked with no arguments and its return value is ignored.
 */
export const pushDismissible = (dismiss, label = "dismissible") => {
  if (typeof dismiss !== "function") {
    throw new TypeError("pushDismissible requires a dismiss function");
  }
  const entry = { id: (nextId += 1), dismiss, label };
  stack = [...stack, entry];
  notifyDepth();
  return () => {
    const before = stack.length;
    stack = stack.filter((e) => e.id !== entry.id);
    // Unregistering twice is legal — React can run a cleanup after the entry is
    // already gone. Only a real removal is a depth change.
    if (stack.length !== before) notifyDepth();
  };
};

/** How many overlays currently claim Back. */
export const dismissibleCount = () => stack.length;

/** True when Back should close something rather than navigate. */
export const hasDismissible = () => stack.length > 0;

/** Labels, oldest first. Diagnostics and tests only — never branch on these. */
export const dismissibleLabels = () => stack.map((e) => e.label);

/**
 * Close the top-most overlay. Returns true when one was closed, false when
 * there was nothing to close and Back should fall through to navigation.
 *
 * The entry is NOT removed here. Removal is the registering component's job,
 * via the cleanup it got from `pushDismissible` — which runs when its `open`
 * prop goes false. Removing it here instead would leave a sheet whose owner
 * declined to close (a confirm-before-discard flow, say) permanently
 * unreachable by Back.
 */
export const dismissTopDismissible = () => {
  const top = stack[stack.length - 1];
  if (!top) return false;
  top.dismiss();
  return true;
};

/**
 * Test-only: drop every registration. Never call this from app code.
 *
 * Subscribers survive — they are consumers of the stack, not entries in it, and
 * a suite that resets between tests must not silently uninstall the Escape
 * listener a component under test just mounted. They ARE told the depth is now
 * zero, so anything holding a listener lets it go.
 */
export const resetDismissibles = () => {
  const had = stack.length > 0;
  stack = [];
  if (had) notifyDepth();
};

/**
 * Register an overlay with the stack for as long as it is open.
 *
 * One line per overlay:
 *   useDismissible(open, onClose, "profile-account-sheet");
 *
 * The callback is held in a ref rather than in the effect's dependency list on
 * purpose. Every call site in this app passes an inline arrow
 * (`onClose={() => setOpen(false)}`), which is a new function on every parent
 * render. With `onClose` as a dependency the effect would tear down and
 * re-register on each of those renders, moving the overlay to the TOP of the
 * stack — so the last sheet whose parent happened to re-render would win Back,
 * instead of the last sheet the user opened. The ref keeps the registration
 * stable and still calls the current callback.
 */
export const useDismissible = (open, onClose, label = "dismissible") => {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    return pushDismissible(() => {
      onCloseRef.current?.();
    }, label);
  }, [open, label]);
};

export default useDismissible;
